import * as userDataJson from './users.json'
import * as d3 from 'd3'

export const onResizeHead = () => {
  let vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

export type SocialType = "Twitter" | "GitHub" | "LinkedIn" | "YouTube"

export interface Social {
  handle: string,
  type: SocialType
}

export interface User {
  name: string,
  number: number,
  index: number,
  description: string,
  socials: [Social]
}

export interface UserList {
  users: Record<string, User>
}

export let userData: UserList = userDataJson
Object.keys(userData.users).forEach(key => userData.users[key].number = parseInt(key))
Object.keys(userData.users).forEach((key, index) => userData.users[key].index = index)

type onUserSelected = (user?: User) => void
let userSelected: User | undefined = undefined
const onUserSelectedCallbacks: onUserSelected[] = []

const twitter_logo = document.getElementById("twitter_logo")?.src
const linkedin_logo = document.getElementById("linkedin_logo")?.src
const github_logo = document.getElementById("github_logo")?.src
const youtube_logo = document.getElementById("youtube_logo")?.src

const getSocialInfo = (social?: Social) => {
  var link = ""
  var img = ""

  switch (social?.type) {
    case "Twitter":
      link = `https://twitter.com/${social.handle}`
      img = twitter_logo
      break
    case "GitHub":
      link = `https://github.com/${social.handle}`
      img = github_logo
      break
    case "LinkedIn":
      link = `https://www.linkedin.com/in/${social.handle}`
      img = linkedin_logo
      break
    case "YouTube":
      link = `https://www.youtube.com/channel/${social.handle}`
      img = youtube_logo
      break
  }

  return {
    link,
    img
  }
}

export const addUserSelected = (onUserSelected: onUserSelected) => {
  onUserSelectedCallbacks.push(onUserSelected)
  onUserSelected(userSelected)
}

const setUserSelected = (user?: User) => {
  if (user == userSelected) {
    return
  }
  onUserSelectedCallbacks.forEach((onUserSelected: onUserSelected) => onUserSelected(user))

  userSelected = user
}

enum DeviceEnum {
  Desktop,
  Mobile
}

const getDevice = () => window.innerWidth > window.innerHeight ? DeviceEnum.Desktop : DeviceEnum.Mobile

var color = "#ffffff"
var color_selected = "#FF7900"
if (parseInt(window.location.hash.substring(1)) == 4) {
  color = "#fc199a"
  color_selected = "#2CE88A"
  document.getElementById("background")!.style.backgroundImage = "linear-gradient(to bottom, #200933 75%, #3d0b43)"
}

let spacing = 100
const circle_dia = 40
const logo_dia = circle_dia - 5
let first_entry_offset = 0

let width = 0, height = 0, min_scroll_distance = 0, coin_center = 0, max_text_width = 0, r = 0
let a = (Math.PI * 2) * (270 / 360)

const update_size = () => {
  switch (getDevice()) {
    case DeviceEnum.Desktop:
      {
        document.documentElement.style.setProperty('--convas-margin-right', `${30 + circle_dia}px`);

        break
      }
    case DeviceEnum.Mobile:
      {
        document.documentElement.style.setProperty('--convas-margin-right', `30px`);

        break
      }
  }

  width = document.getElementById("content")?.offsetWidth ?? 0
  height = window.innerHeight
  first_entry_offset = spacing - height / 2
  svg.attr("width", width)
    .attr("height", height)


  const text_section_height = document.getElementById("text-section")?.getBoundingClientRect()?.height ?? 0

  if (getDevice() == DeviceEnum.Desktop) {
    min_scroll_distance = (text_section_height ?? 0) - height / 3
  } else {
    min_scroll_distance = (text_section_height ?? 0) - height / 4
  }

  const coin = document.getElementById("coin")?.getBoundingClientRect()
  const svg_ = document.querySelector("svg")?.getBoundingClientRect()

  if (coin && svg_) {
    coin_center = coin.left + coin.width / 2 - svg_.left
    switch (getDevice()) {
      case DeviceEnum.Desktop:
        r = coin.width / 2// - circle_dia / 2 - 10
        break
      case DeviceEnum.Mobile:
        r = coin.width / 2
        break
    }
  }

  const content_height = min_scroll_distance + (Object.keys(userData.users).length - 1) * spacing + height - first_entry_offset
  document.documentElement.style.setProperty('--content_height', `${content_height}px`);
}

const scrollToUser = (number: number) => {
  window.scrollTo({
    top: (number - 1) * spacing + min_scroll_distance - first_entry_offset,
    behavior: 'smooth'
  })
}

let kl = 260
var svg = d3.select("#user_container")
  .append("svg")
  .attr("xmlns", "http://www.w3.org/2000/svg")
update_size()

let intersections = (m: number, c: number, a: number, b: number) => {
  let a_sq = a * a;
  let b_sq = b * b;
  let c_sq = c * c;
  let m_sq = m * m;
  let div = (a_sq * m_sq + b_sq);
  let inner_sq = Math.sqrt(div - c_sq)

  let x_inner = a * b * inner_sq
  let x_outer = -(a_sq) * m * c
  let x1 = (x_outer + x_inner) / div
  let x2 = (x_outer - x_inner) / div

  let y_inner = m * x_inner
  let y_outer = b_sq * c
  let y1 = (y_outer + y_inner) / div
  let y2 = (y_outer - y_inner) / div

  return {
    x1, x2, y1, y2
  }
}

let mc = (x1: number, x2: number, y1: number, y2: number) => {
  let m = (y2 - y1) / (x2 - x1)
  let c = 0
  return {
    m, c
  }
}

let distance = (cx: number, cy: number, a: number, b: number, x1: number, x2: number, y1: number, y2: number) => {
  let { m, c } = mc(x1 - cx, x2 - cx, y1 - cy, x2 - cy)
  let intersection_results = intersections(m, c, a, b)
  let dx = intersection_results.x1
  let dy = intersection_results.y1
  return Math.sqrt(dx * dx + dy * dy)
}

const getPosition = (x: number, d: User, scroll: number) => {
  const x1 = x
  const x2 = coin_center
  const y1 = 0
  const y2 = - (d.index * spacing - scroll - first_entry_offset)

  let l = Math.sqrt((y2 - y1) * (y2 - y1) + (x2 - x1) * (x2 - x1))
  let dist = l - distance(width / 2, height / 2, height / 2, width / 4, x1, x2, y1, y2)

  const ret_x = x1 + ((x2 - x1) / l) * dist
  const ret_y = y1 + ((y2 - y1) / l) * dist
  return {
    x: ret_x,
    y: ret_y
  }
}

const arc_group = svg.append("g")
  .attr("fill", color_selected)
  .attr("stroke", color_selected)
  .attr("stroke-width", 5)
  .attr("stroke-linecap", "round")

let arc = arc_group
  .append("path")
  .attr("fill", "none")

let arc_joiner = arc_group
  .append("line")

let arc_diagonal = arc_group
  .append("line")

const description_line = arc_group
  .append("line")

const description_foreign = arc_group
  .append("foreignObject")
  .attr("height", height)

const description = description_foreign
  .append("xhtml:p")
  .attr("xmlns:xhtml", "http://www.w3.org/1999/xhtml")
  .style("color", color_selected)
  .style("overflow-y", "auto")

const social_group = svg.append("g")
  .attr("fill", "none")
  .attr("stroke", color_selected)
  .attr("stroke-width", 5)
  .attr("stroke-linecap", "round")

interface SoicalDataType {
  id: number
  social?: Social
}

var social_data: SoicalDataType[] = [
  { id: 0 },
  { id: 1 },
  { id: 2 },
  { id: 3 },
]

const social_lines = social_group
  .selectAll("line")
  .data(social_data)
  .enter()
  .append("line")

const social_circles = social_group
  .selectAll("circle")
  .data(social_data)
  .enter()
  .append("circle")
  .attr("fill", color_selected)

const isVisibile = (y: number) => y < height / 2 + first_entry_offset + 5 && y > -5 - height / 2 + first_entry_offset

var gs = svg.append("g").selectAll('g')
  .data(Object.values(userData.users))
  .enter()
  .append("g")
  .attr("class", "user")
  .attr("stroke-linecap", "round")

var horizontal_lines = gs.append("line")
  .attr("x1", 5)
  .attr("y1", 0)
  .attr("y2", 0)
  .attr("stroke-width", 5)

var pointing_lines = gs.append("line")
  .attr("y1", 0)
  .attr("stroke-width", 5)

var texts = gs
  .append("text")
  .text((d: User) => `#${d.number} ${d.name}`)
  .attr("dy", -10)
  .attr("dx", 10)
  .attr("stroke-width", 0)

var click_lines = gs.append("rect")
  .attr("height", 50)
  .attr("x", 5)
  .attr("y", -25)
  .on("click", (event: Event, d: User) => scrollToUser(d.index + 1))
  .style("cursor", "pointer")
  .attr("fill", "#ffffff")
  .style("opacity", 0)
  .attr("stroke-width", 0)

max_text_width = texts.nodes().map(node => node.getComputedTextLength()).reduce((l1: number, l2: number) => Math.max(l1, l2))

const calc_lines = (scroll: number) => {
  const x = Math.max(width / 2 - kl, max_text_width)
  const angle_offset_factor = 0.95

  a = (Math.PI + Math.atan2(height / 2, x - coin_center)) * angle_offset_factor

  let dx = (x + (a / angle_offset_factor - a) * r) - (coin_center - Math.cos(a) * r)
  let dy = -(height / 2 + Math.sin(a) * r)
  let len = Math.sqrt(dx * dx + dy * dy)

  const description_height = description.node()?.clientHeight ?? 0

  var off = 0

  switch (getDevice()) {
    case DeviceEnum.Desktop:
      off = Math.max(90 - Math.sin(a) * description_height, width - coin_center + r)
      break
    case DeviceEnum.Mobile:
      off = Math.min(90 - Math.sin(a) * description_height, height / 2 - r)
      break
  }

  var arc_diagonal_x1 = coin_center - Math.cos(a) * r + (dx * (1 - (off / len)))
  var arc_diagonal_y1 = height / 2 + Math.sin(a) * r + (dy * (1 - (off / len)))

  if (arc_diagonal_y1 > height / 2 - r - 10) {
    off = (1 - (-r - 10 - Math.sin(a) * r) / dy) * len
  }

  arc_diagonal_x1 = coin_center - Math.cos(a) * r + (dx * (1 - (off / len)))
  arc_diagonal_y1 = height / 2 + Math.sin(a) * r + (dy * (1 - (off / len)))

  const max_description_height = arc_diagonal_y1 - 10 * 2

  arc_diagonal.attr("x1", arc_diagonal_x1)
    .attr("y1", arc_diagonal_y1)
    .attr("x2", coin_center - Math.cos(a) * r)
    .attr("y2", height / 2 + Math.sin(a) * r)

  description.style("max-height", `${max_description_height}px`)

  const description_line_end = width - 10 - circle_dia / 2

  description_foreign
    .attr("x", arc_diagonal_x1)
    .attr("y", Math.max(arc_diagonal_y1 - description_height - 10, 10))
    .attr("width", description_line_end - arc_diagonal_x1)

  description_line
    .attr("x1", arc_diagonal_x1)
    .attr("y1", arc_diagonal_y1)
    .attr("x2", description_line_end)
    .attr("y2", arc_diagonal_y1)

  pointing_lines.attr("x1", x)
  pointing_lines.attr("y2", (d: User) => getPosition(x, d, scroll).y)
  pointing_lines.attr("x2", (d: User) => getPosition(x, d, scroll).x)
  horizontal_lines.attr("x2", x)
  click_lines.attr("width", x)
  if (userSelected) {
    let pos = getPosition(x, userSelected, scroll)
    let svg_y = pos.y + userSelected.index * spacing - scroll + height / 2 - first_entry_offset
    let x_end = coin_center - Math.cos(Math.asin((svg_y - height / 2) / r)) * r
    if (x_end == x_end) {
      arc_joiner.attr("x1", pos.x)
        .attr("x2", x_end)
        .attr("y1", svg_y)
        .attr("y2", svg_y)
      arc.attr("d", `M ${x_end} ${svg_y} A ${r} ${r} 0 ${a > Math.PI ? 1 : 0} 0 ${coin_center - Math.cos(a) * r} ${height / 2 + Math.sin(a) * r}`)
    }
  }
  arc_group.attr("visibility", userSelected ? "visible" : "hidden")
  social_group.attr("visibility", userSelected ? "visible" : "hidden")
  gs.attr("stroke", (d: User) => userSelected == d ? color_selected : color)
  gs.attr("fill", (d: User) => userSelected == d ? color_selected : color)
  gs.attr("transform", (d: User) => `translate(0, ${d.index * spacing - scroll + height / 2 - first_entry_offset})`)
  click_lines.attr("pointer-events", (d: User) => isVisibile(d.index * spacing - scroll) ? "bounding-box" : "none")
  pointing_lines.attr("visibility", (d: User) => isVisibile(d.index * spacing - scroll) ? "visible" : "hidden")

  switch (getDevice()) {
    case DeviceEnum.Desktop:
      {
        const segment_height = Math.max((height / 2 - arc_diagonal_y1) / social_data.length - circle_dia, 10)

        social_lines
          .attr("x1", description_line_end)
          .attr("x2", description_line_end)
          .attr("y1", (d: SoicalDataType) => (segment_height + circle_dia) * d.id + arc_diagonal_y1)
          .attr("y2", (d: SoicalDataType) => (segment_height + circle_dia) * d.id + arc_diagonal_y1 + segment_height)

        social_circles
          .attr("cx", description_line_end)
          .attr("cy", (d: SoicalDataType) => (segment_height + circle_dia) * d.id + arc_diagonal_y1 + segment_height + circle_dia / 2)
          .attr("r", circle_dia / 2)

        social_group.selectAll("foreignObject")
          .attr("x", description_line_end - logo_dia / 2)
          .attr("y", (d: SoicalDataType) => (segment_height + circle_dia) * d.id + arc_diagonal_y1 + segment_height + circle_dia / 2 - logo_dia / 2)
          .attr("width", logo_dia)
          .attr("height", logo_dia)
        break
      }
    case DeviceEnum.Mobile:
      {
        const segment_height = Math.max((height - 10 - (height / 2 + r)) / social_data.length - circle_dia, 10)

        social_lines
          .attr("x1", coin_center)
          .attr("x2", coin_center)
          .attr("y1", (d: SoicalDataType) => (segment_height + circle_dia) * d.id + height / 2 + r)
          .attr("y2", (d: SoicalDataType) => (segment_height + circle_dia) * d.id + height / 2 + r + segment_height)

        social_circles
          .attr("cx", coin_center)
          .attr("cy", (d: SoicalDataType) => (segment_height + circle_dia) * d.id + height / 2 + r + segment_height + circle_dia / 2)
          .attr("r", circle_dia / 2)

        social_group.selectAll("foreignObject")
          .attr("x", coin_center - logo_dia / 2)
          .attr("y", (d: SoicalDataType) => (segment_height + circle_dia) * d.id + height / 2 + r + segment_height + circle_dia / 2 - logo_dia / 2)
          .attr("width", logo_dia)
          .attr("height", logo_dia)

        break
      }
  }
}

const onScroll = () => {
  const scrolled = Math.max(window.scrollY - min_scroll_distance, 0)

  const user_number = Math.round((scrolled + first_entry_offset) / spacing)
  setUserSelected(Object.values(userData.users)[user_number])

  calc_lines(scrolled)
}

window.addEventListener('scroll', () => onScroll())

window.addEventListener('resize', () => {
  onResizeHead()
  update_size()
  onScroll()
})

window.addEventListener("hashchange", () => {
  scrollToUser(parseInt(window.location.hash.substring(1)))
})

scrollToUser(parseInt(window.location.hash.substring(1)))

addUserSelected((user?: User) => {
  social_data.forEach(data => data.social = undefined)

  if (user) {
    description.text(user.description)

    social_circles.attr("visibility", (d: SoicalDataType) => d.id < (user.socials?.length ?? 0) ? "visible" : "hidden")
    social_lines.attr("visibility", (d: SoicalDataType) => d.id < (user.socials?.length ?? 0) ? "visible" : "hidden")

    user.socials?.forEach((value: Social, index: number) => social_data[index].social = value)
  } else {
    description.text("")
    social_circles.attr("visibility", "hidden")
    social_lines.attr("visibility", "hidden")
  }

  social_group
    .selectAll("foreignObject")
    .data(social_data)
    .join(
      (enter) => {
        return enter.append("foreignObject")
          .append("xhtml:a")
          .attr("xmlns:xhtml", "http://www.w3.org/1999/xhtml")
          .attr("target", "_blank")
          .append('xhtml:div')
          .attr("style", `position: relative; height: ${logo_dia}px; width: ${logo_dia}px;`)
          .append("xhtml:img")
          .attr("xmlns:xhtml", "http://www.w3.org/1999/xhtml")
          .attr("style", "position: absolute; top: 0; bottom: 0; right: 0; left: 0; margin: auto;")
      },
      (update) => {
        update
          .selectAll("a")
          .attr("href", (d: SoicalDataType) => getSocialInfo(d?.social).link)

        update
          .selectAll("img")
          .attr("src", (d: SoicalDataType) => getSocialInfo(d?.social).img)

        update
          .selectAll("a")
          .attr("style", (d: SoicalDataType) => `display: ${getSocialInfo(d?.social).img != "" ? "block" : "none"}`)
        return update
      },
      (exit) => {
        return exit.remove()
      }
    )
})

const resizeObserver = new ResizeObserver(() => {
  onScroll()
})
resizeObserver.observe(description.node())

onScroll()
